import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";

const QUICK = ["Gift recommendations", "Track my order", "Delivery info", "Payment options", "Exchange policy", "Talk to support"];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your Savyron Gift Assistant. Looking for a gift, checking an order, or need help?" },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const sessionRef = useRef(localStorage.getItem("savyron_chat_session") || (() => {
    const s = crypto.randomUUID(); localStorage.setItem("savyron_chat_session", s); return s;
  })());
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || typing) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setTyping(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setTyping(false);
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm having trouble connecting. Please write to founder@savyron.in." };
        return copy;
      });
    }
    setTyping(false);
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[65] flex items-center gap-2 bg-gradient-to-br from-[#C9A24B] to-[#5C1230] text-[#FFFFFF] rounded-full pl-4 pr-5 py-3 shadow-[0_10px_30px_rgba(200,164,93,0.35)] hover:shadow-[0_14px_40px_rgba(200,164,93,0.5)] transition-shadow animate-floaty"
        data-testid="ai-concierge-toggle" whileTap={{ scale: 0.95 }}>
        <Sparkles size={17} />
        <span className="text-xs font-semibold tracking-wider uppercase">Ask Savyron</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-5 right-5 z-[75] w-[calc(100vw-2.5rem)] max-w-sm h-[540px] max-h-[75vh] bg-[#FBF7F1] border border-[#7E1E3F]/30 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden"
            data-testid="ai-chat-drawer">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#7E1E3F]/15 bg-[#FFFFFF]/60">
              <div>
                <p className="font-serif-lux text-lg text-[#2E1A22]">Savyron Gift Assistant</p>
                <p className="text-[10px] font-mono-lux tracking-[0.2em] text-[#7E1E3F] uppercase">Always at your service</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#857078] hover:text-[#2E1A22]" data-testid="ai-chat-close"><X size={18} /></button>
            </div>
            <div ref={bodyRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                  className={`max-w-[85%] text-sm leading-relaxed rounded-2xl px-4 py-2.5 ${m.role === "user" ? "ml-auto bg-[#7E1E3F] text-[#FFFFFF] rounded-br-md" : "bg-[#FFFFFF] text-[#45303A] border border-[#7E1E3F]/15 rounded-bl-md"}`}>
                  {m.content || (typing && i === messages.length - 1 ? (
                    <span className="flex gap-1 py-1">
                      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#7E1E3F]" />
                      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#7E1E3F]" style={{ animationDelay: "0.15s" }} />
                      <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[#7E1E3F]" style={{ animationDelay: "0.3s" }} />
                    </span>
                  ) : null)}
                </motion.div>
              ))}
            </div>
            <div className="px-4 pt-2 flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
              {QUICK.map((q) => (
                <button key={q} onClick={() => send(q)}
                  className="shrink-0 text-[11px] border border-[#7E1E3F]/35 text-[#5C1230] rounded-full px-3 py-1.5 hover:bg-[#7E1E3F]/10 transition-colors"
                  data-testid={`quick-reply-${q.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {q}
                </button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 pt-2 flex gap-2 border-t border-[#7E1E3F]/10">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about gifts, orders, delivery…"
                className="flex-1 bg-[#FFFFFF] border border-[#7E1E3F]/25 rounded-full px-4 py-2.5 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F]/60 placeholder:text-[#857078]"
                data-testid="ai-chat-input" />
              <button type="submit" className="p-2.5 bg-[#7E1E3F] text-[#FFFFFF] rounded-full hover:bg-[#5C1230] transition-colors" data-testid="ai-chat-send-btn" aria-label="Send">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
